import { useState, useEffect, useCallback, useRef } from 'react';
import { DEFAULT_STATES } from '../data/defaultStates';
import { transformListings, updateListingWithPropertyData } from '../utils/dataTransformers';
import { sortListings, toggleSortDirection } from '../utils/sortingLogic';
import { initializeQueue, processQueueItem } from '../utils/queueManager';
import propertyService from '../../../services/propertyService';

export const useListingsTable = (listings, pagination, onPageChange) => {
  // State management
  const [fetchedPropertyData, setFetchedPropertyData] = useState(DEFAULT_STATES.fetchedPropertyData);
  const [expandedRows, setExpandedRows] = useState(DEFAULT_STATES.expandedRows);
  const [sortConfig, setSortConfig] = useState(DEFAULT_STATES.sortConfig);
  const [displayedListings, setDisplayedListings] = useState(DEFAULT_STATES.displayedListings);
  const [requestQueue, setRequestQueue] = useState(DEFAULT_STATES.requestQueue);
  const [isProcessingQueue, setIsProcessingQueue] = useState(DEFAULT_STATES.isProcessingQueue);
  const [failedRequests, setFailedRequests] = useState(DEFAULT_STATES.failedRequests);

  const [isAutoRefreshing, setIsAutoRefreshing] = useState(DEFAULT_STATES.isAutoRefreshing);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(DEFAULT_STATES.estimatedTimeRemaining);

  // Refs
  const processingTimeoutRef = useRef(null);
  const queueInitializedRef = useRef(false);
  const autoRefreshInterval = useRef(null);
  const cacheLoadedRef = useRef(false);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((listingId) => {
    setExpandedRows(prev => ({
      ...prev,
      [listingId]: !prev[listingId]
    }));
  }, []);

  // Handle sorting
  const requestSort = useCallback((key) => {
    setSortConfig(prevConfig => toggleSortDirection(prevConfig, key));
  }, []);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    
    // Calculate the first item of the current page
    const currentFirstItem = pagination ? (pagination.currentPage - 1) * pagination.pageSize + 1 : 1;
    
    // Calculate which page this item would be on with the new page size
    const newPage = Math.ceil(currentFirstItem / newRowsPerPage);
    
    // Call onPageChange with the new page number and page size
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  }, [pagination, onPageChange]);

  // Queue property data fetch
  const queuePropertyDataFetch = useCallback((listingId, address, city, state) => {
    setRequestQueue(prev => {
      // If queue is already at max size, don't add more
      if (prev.length >= 10) {
        return prev;
      }
      
      // Check if this listing is already in the queue
      if (prev.some(item => item.listingId === listingId)) {
        return prev;
      }

      const newQueue = [...prev, { listingId, address, city, state }];
      // Calculate position based on new queue length
      const position = newQueue.length;
      
      // Update status in the same batch
      setFetchedPropertyData(prevData => ({
        ...prevData,
        [listingId]: {
          status: 'queued',
          queuePosition: position,
          estimatedWaitTime: position * 1.1
        }
      }));
      
      return newQueue;
    });
  }, []);

  // Process queue
  const processQueue = useCallback(async () => {
    await processQueueItem(
      requestQueue,
      isProcessingQueue,
      failedRequests,
      fetchedPropertyData,
      setRequestQueue,
      setIsProcessingQueue,
      setFailedRequests,
      setFetchedPropertyData,
      processingTimeoutRef
    );
  }, [requestQueue, isProcessingQueue, failedRequests, fetchedPropertyData]);

  // Cache-first data loading effect
  useEffect(() => {
    const loadCachedDataFirst = async () => {
      if (!listings?.length || cacheLoadedRef.current) return;
      
      // Removed debug logging
      
      try {
        // Get all property IDs
        const propertyIds = listings.map(l => l.id || l.property_id).filter(Boolean);
        
        if (propertyIds.length === 0) return;
        
        // Fetch all cached data at once - this should be instant for cached data
        const cachedData = await propertyService.getBulkCachedPropertyData(propertyIds);
        
        // Removed debug logging
        
        // Update state with cached data immediately
        setFetchedPropertyData(prev => ({
          ...prev,
          ...cachedData
        }));
        
        // Only queue properties that don't have cached data
        const uncachedListings = listings.filter(listing => {
          const listingId = listing.id || listing.property_id;
          return !cachedData[listingId] && listing.address && listing.city && listing.state;
        });
        
        // Removed debug logging
        
        if (uncachedListings.length > 0) {
          const { queue, statusUpdates } = initializeQueue(uncachedListings, cachedData, failedRequests);
          
          if (queue.length > 0) {
            setRequestQueue(queue);
            setFetchedPropertyData(prev => ({ ...prev, ...statusUpdates }));
          }
        }
        
        cacheLoadedRef.current = true;
        
      } catch (error) {
        console.error('❌ Error loading cached data, falling back to queue-all approach:', error);
        
        // Fallback to original queue-all approach if cache loading fails
        const { queue, statusUpdates } = initializeQueue(listings, fetchedPropertyData, failedRequests);
        
        if (queue.length > 0) {
          setRequestQueue(queue);
          setFetchedPropertyData(prev => ({ ...prev, ...statusUpdates }));
        }
        
        cacheLoadedRef.current = true;
      }
    };

    loadCachedDataFirst();
  }, [listings, failedRequests]);

  // Transform listings effect
  useEffect(() => {
    if (!listings?.length) {
      // Reset displayedListings to empty array when no listings
      setDisplayedListings([]);
      return;
    }

    const transformed = transformListings(listings);
    const sorted = sortListings(transformed, sortConfig);
    setDisplayedListings(sorted);
  }, [listings, sortConfig]);

  // Update listings with property data effect
  useEffect(() => {
    const updatedListings = displayedListings.map(listing => {
      const listingId = listing.id || listing.property_id;
      const propertyData = fetchedPropertyData[listingId];
      return updateListingWithPropertyData(listing, propertyData);
    });
    
    // If sorting by Zillow-dependent fields, resort
    if (['zillow_estimate', 'profit', 'downpayment'].includes(sortConfig.key)) {
      const resorted = sortListings(updatedListings, sortConfig);
      setDisplayedListings(resorted);
    } else {
      setDisplayedListings(updatedListings);
    }
  }, [fetchedPropertyData, sortConfig]);

  // Process queue when it changes
  useEffect(() => {
    if (requestQueue.length > 0 && !isProcessingQueue) {
      processQueue();
    }
  }, [requestQueue, isProcessingQueue, processQueue]);

  // Reset cache loaded flag when page changes
  useEffect(() => {
    cacheLoadedRef.current = false;
  }, [pagination?.currentPage]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, []);

  return {
    // State
    fetchedPropertyData,
    expandedRows,
    sortConfig,
    displayedListings,
    requestQueue,
    isProcessingQueue,
    failedRequests,
    isAutoRefreshing,
    estimatedTimeRemaining,
    
    // Actions
    toggleRowExpansion,
    requestSort,
    handleRowsPerPageChange,
    queuePropertyDataFetch,
    
    // Setters for modals and UI state
    setIsAutoRefreshing
  };
};